import React, { useState } from 'react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock, Send, Rotate3d, ArrowUpRight } from 'lucide-react';
import { SITE } from '../data/mock';
import { api } from '../lib/api';
import { usePublicSettings } from '../hooks/usePublicSettings';
import { EditRegion } from '../components/AdminEditBar';
import { InlineText } from '../components/InlineEditable';
import PageHero from '../components/PageHero';

export default function Contact() {
  useSEO('contact');
  const settings = usePublicSettings();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error('Lütfen gerekli alanları doldurun.'); return; }
    try {
      await api.createMessage(form);
      toast.success('Mesajınız alındı. En kısa sürede dönüş yapacağız.');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Mesaj gönderilemedi.');
    }
  };

  const phone = settings.site_phone || SITE.phone;
  const mail = settings.site_email || SITE.email;
  const address = settings.site_address || SITE.address;
  const hours = settings.site_working_hours || 'Pzt – Cum: 09:00 – 18:00';
  const mapUrl = settings.site_map_iframe || 'https://www.openstreetmap.org/export/embed.html?bbox=28.9874%2C41.0659%2C29.0074%2C41.0759&layer=mapnik';
  const tourUrl = (settings.virtual_tour_url ?? 'https://yekuv.org/yekuv360/').trim();
  return (
    <EditRegion label="İletişim Bilgileri" to="/admin/settings">
      <>
      <PageHero slug="contact"
        kicker="İletişim"
        title={<>Bize <span className="italic text-coral-200">yazın</span>, sohbet edelim.</>}
        text="Sorularınız, önerileriniz veya işbirliği talepleriniz için buradayız."
      />

      <section className="bg-cream">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="space-y-5">
              {[
                { icon: MapPin, title: 'Adres', text: address },
                { icon: Phone, title: 'Telefon', text: phone },
                { icon: Mail, title: 'E-posta', text: mail },
                { icon: Clock, title: 'Çalışma Saatleri', text: hours },
              ].map((c, i) => {
                const Icon = c.icon;
                return (
                  <div key={i} className="bg-white rounded-2xl p-6 border border-ink-100 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-ink-50 grid place-items-center text-ink-600 shrink-0"><Icon className="w-5 h-5" /></div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-ink-400 font-semibold">{c.title}</div>
                      <div className="mt-1 text-ink-600 font-medium">{c.text}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Virtual tour card — link managed in Site Ayarları, texts inline-editable */}
            {tourUrl && (
              <a
                href={tourUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="virtual-tour-card"
                className="group mt-5 block relative overflow-hidden rounded-2xl bg-ink-600 text-cream p-6 border border-ink-600 hover:shadow-xl hover:shadow-ink-900/20 transition-all hover:-translate-y-0.5"
              >
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-gold/20 blur-2xl pointer-events-none" />
                <div className="relative flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gold text-ink-700 grid place-items-center shrink-0 group-hover:scale-110 transition-transform">
                    <Rotate3d className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <InlineText settingKey="virtual_tour_title" fallback="YEKÜV 360° Sanal Tur" as="div"
                      className="font-display text-xl leading-tight" />
                    <InlineText settingKey="virtual_tour_text" fallback="Vakıf merkezimizi 360° sanal turla gezin" as="div"
                      className="mt-0.5 text-xs text-cream/70" />
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-gold shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
              </a>
            )}

            <div className="mt-6 rounded-3xl overflow-hidden border border-ink-100">
              <iframe
                title="YEKÜV Konumu"
                width="100%"
                height="320"
                loading="lazy"
                style={{ border: 0 }}
                src={mapUrl}
              />
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 bg-white rounded-3xl p-8 md:p-10 border border-ink-100 shadow-xl shadow-ink-900/5">
            <InlineText settingKey="contact_form_title" fallback="Gönder bir mesaj" as="h2"
              className="font-display text-3xl md:text-4xl text-ink-600" />
            <InlineText settingKey="contact_form_subtitle" fallback="Sorularınız, önerileriniz veya işbirliği talepleriniz için." as="p"
              multiline className="text-ink-400 text-sm mt-2" />
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <F label="Ad Soyad *" value={form.name} onChange={v => setForm({ ...form, name: v })} />
              <F label="E-posta *" type="email" value={form.email} onChange={v => setForm({ ...form, email: v })} />
            </div>
            <div className="mt-4">
              <F label="Konu" value={form.subject} onChange={v => setForm({ ...form, subject: v })} />
            </div>
            <div className="mt-4">
              <label className="text-xs uppercase tracking-wider text-ink-400 font-semibold">Mesajınız *</label>
              <textarea rows={6} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="mt-1.5 w-full p-4 rounded-xl bg-cream-200 border border-ink-100 focus:border-ink-500 focus:ring-2 focus:ring-ink-500/10 outline-none text-ink-600" />
            </div>
            <button className="mt-6 inline-flex items-center gap-2 bg-ink-600 text-cream rounded-full px-8 h-12 font-semibold hover:bg-ink-500 transition-colors"><Send className="w-4 h-4" /> Gönder</button>
          </form>
        </div>
      </section>
    </>
    </EditRegion>
  );
}

function F({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-ink-400 font-semibold">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} className="mt-1.5 w-full h-12 px-4 rounded-xl bg-cream-200 border border-ink-100 focus:border-ink-500 focus:ring-2 focus:ring-ink-500/10 outline-none text-ink-600" />
    </div>
  );
}
